// src/components/MaintenanceLists/ObservationModal.js
import React from 'react';
import { Form, Button } from 'react-bootstrap';
import Modal from '../Modal';

/**
 * Modal component for adding/editing observation records
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {string|null} props.editingItemId - ID of item being edited, or null if adding new
 * @param {Object} props.observationItem - Observation item data
 * @param {Function} props.setObservationItem - Function to update observation item data
 * @param {Function} props.addObservationItem - Function to add a new observation item
 * @param {Function} props.editObservationItem - Function to edit an existing observation item
 * @returns {JSX.Element} Rendered component
 */
const ObservationModal = ({
  isOpen,
  onClose,
  editingItemId,
  observationItem,
  setObservationItem,
  addObservationItem,
  editObservationItem
}) => {
  const isEditing = !!editingItemId;
  const modalTitle = isEditing ? "Edit Observation Record" : "Add Observation Record";
  const submitLabel = isEditing ? "Update Observation" : "Add Observation";
  
  // Reset form function
  const resetForm = () => {
    setObservationItem({
      date: new Date().toISOString().split('T')[0],
      odometer: '',
      description: '',
      severity: 'medium',
      status: 'open',
      notes: ''
    });
  };
  
  const handleClose = () => {
    onClose();
    resetForm();
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
    >
      <div className="observation-modal">
        <Form onSubmit={(e) => {
          e.preventDefault();
          isEditing ? editObservationItem() : addObservationItem();
        }}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="observation-date">Date *</Form.Label>
            <Form.Control
              id="observation-date"
              type="date"
              value={observationItem.date}
              onChange={(e) => setObservationItem({...observationItem, date: e.target.value})}
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label htmlFor="observation-odometer">Odometer (miles/km)</Form.Label>
            <Form.Control
              id="observation-odometer"
              type="number"
              min="0"
              placeholder="Current odometer reading"
              value={observationItem.odometer}
              onChange={(e) => setObservationItem({...observationItem, odometer: e.target.value})}
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label htmlFor="observation-description">Description *</Form.Label>
            <Form.Control
              as="textarea"
              id="observation-description"
              rows={3}
              placeholder="Describe what you've observed"
              value={observationItem.description}
              onChange={(e) => setObservationItem({...observationItem, description: e.target.value})}
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label htmlFor="observation-severity">Severity *</Form.Label>
            <Form.Select
              id="observation-severity"
              value={observationItem.severity}
              onChange={(e) => setObservationItem({...observationItem, severity: e.target.value})}
              required
            >
              <option value="low">Low (Just noting)</option>
              <option value="medium">Medium (Monitor)</option>
              <option value="high">High (Needs attention)</option>
              <option value="critical">Critical (Immediate action)</option>
            </Form.Select>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label htmlFor="observation-status">Status *</Form.Label>
            <Form.Select
              id="observation-status"
              value={observationItem.status}
              onChange={(e) => setObservationItem({...observationItem, status: e.target.value})}
              required
            >
              <option value="open">Open</option>
              <option value="monitoring">Monitoring</option>
              <option value="resolved">Resolved</option>
            </Form.Select>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label htmlFor="observation-notes">Notes</Form.Label>
            <Form.Control
              as="textarea"
              id="observation-notes"
              rows={3}
              placeholder="Additional details or notes"
              value={observationItem.notes}
              onChange={(e) => setObservationItem({...observationItem, notes: e.target.value})}
            />
          </Form.Group>
          
          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button 
              variant="success" 
              type="submit"
              disabled={!observationItem.date || !observationItem.description}
            >
              {submitLabel}
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default ObservationModal;